import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { supabase } from '../../../lib/supabaseClient';
import Spinner from '../../Atoms/Spinner';
import toast from 'react-hot-toast';
import { SearchPotentialMatches, SearchPotentialProjects } from '../../../api';

const Container = styled.div`
    max-width: 1600px;
    margin: 0 auto;
    padding: 40px 20px;
`;

const Header = styled.div`
    margin-bottom: 40px;

    h1 {
        font-size: 32px;
        margin-bottom: 8px;
        color: #333;
    }

    .subtitle {
        font-size: 18px;
        color: #666;
    }
`;

const BackButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background: #f5f5f5;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    color: #333;
    transition: all 0.2s ease;
    margin-bottom: 20px;

    &:hover {
        background: #e8e8e8;
        transform: translateX(-4px);
    }
`;

const StatsSection = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 20px;
    margin-bottom: 40px;
`;

const StatCard = styled.div`
    background: ${props => props.color || 'white'};
    padding: 24px;
    border-radius: 12px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);

    .value {
        font-size: 36px;
        font-weight: 700;
        color: ${props => props.textColor || '#333'};
    }

    .label {
        font-size: 14px;
        color: ${props => props.textColor ? `${props.textColor}aa` : '#666'};
        margin-top: 8px;
    }
`;

const TableSection = styled.div`
    background: white;
    border-radius: 16px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.08);
    overflow: hidden;
`;

const TableHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 20px 24px;
    border-bottom: 1px solid #eee;
    flex-wrap: wrap;
    gap: 16px;

    h2 {
        font-size: 20px;
        font-weight: 600;
        margin: 0;
    }
`;

const SearchInput = styled.input`
    padding: 10px 16px;
    border: 1px solid #ddd;
    border-radius: 8px;
    font-size: 14px;
    width: 300px;

    &:focus {
        outline: none;
        border-color: #f39c12;
    }
`;

const Table = styled.table`
    width: 100%;
    border-collapse: collapse;

    th {
        text-align: left;
        padding: 16px 12px;
        background: #f8f9fa;
        font-weight: 600;
        font-size: 13px;
        color: #555;
        border-bottom: 2px solid #eee;
    }

    td {
        padding: 12px;
        border-bottom: 1px solid #f0f0f0;
        font-size: 13px;
        color: #333;
    }

    tr:hover td {
        background: #f8f9fa;
    }
`;

const StatusBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    border-radius: 20px;
    font-size: 12px;
    font-weight: 500;

    &.onboarded {
        background: #d4edda;
        color: #155724;
    }

    &.pending {
        background: #fff3cd;
        color: #856404;
    }
`;

const ActionButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 6px;
    padding: 6px 12px;
    background: ${props => props.variant === 'primary' ? '#f39c12' : '#e7f3ff'};
    color: ${props => props.variant === 'primary' ? 'white' : '#004085'};
    border: none;
    border-radius: 6px;
    font-size: 12px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        opacity: 0.9;
        transform: translateY(-1px);
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const RefreshButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background: #f39c12;
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    transition: all 0.2s ease;

    &:hover {
        background: #e67e22;
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
`;

const ExportButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 10px 20px;
    background: #28a745;
    color: white;
    border: none;
    border-radius: 8px;
    cursor: pointer;
    font-size: 14px;
    transition: all 0.2s ease;
    margin-left: 12px;

    &:hover {
        background: #218838;
    }
`;

const LastUpdate = styled.div`
    font-size: 13px;
    color: #888;
    margin-top: 16px;
    text-align: right;
    padding-right: 24px;
`;

const ModalOverlay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
`;

const ModalContent = styled.div`
    background: white;
    border-radius: 16px;
    padding: 24px;
    max-width: 600px;
    width: 90%;
    max-height: 80vh;
    overflow-y: auto;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const ModalHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 20px;
    padding-bottom: 16px;
    border-bottom: 1px solid #eee;

    h3 {
        margin: 0;
        font-size: 18px;
        color: #333;
    }
`;

const CloseButton = styled.button`
    background: none;
    border: none;
    font-size: 24px;
    cursor: pointer;
    color: #999;
    padding: 0;
    line-height: 1;

    &:hover {
        color: #333;
    }
`;

const FormGroup = styled.div`
    margin-bottom: 16px;

    label {
        display: block;
        font-size: 13px;
        font-weight: 600;
        color: #555;
        margin-bottom: 6px;
    }

    input, textarea {
        width: 100%;
        padding: 10px 12px;
        border: 1px solid #ddd;
        border-radius: 8px;
        font-size: 14px;
        transition: border-color 0.2s;

        &:focus {
            outline: none;
            border-color: #f39c12;
        }
    }

    textarea {
        min-height: 80px;
        resize: vertical;
    }
`;

const SaveButton = styled.button`
    width: 100%;
    padding: 12px;
    background: linear-gradient(135deg, #f39c12, #e67e22);
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    margin-top: 16px;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(243, 156, 18, 0.4);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none;
    }
`;

const MatchButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    background: ${props => props.hasMatches ? '#17a2b8' : '#6c757d'};
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 11px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        opacity: 0.9;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const MatchBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    background: ${props => props.count > 0 ? '#d1ecf1' : '#f8f9fa'};
    color: ${props => props.count > 0 ? '#0c5460' : '#6c757d'};
    border-radius: 12px;
    font-size: 11px;
    font-weight: 500;
`;

const MatchModalContent = styled.div`
    background: white;
    border-radius: 16px;
    padding: 24px;
    max-width: 700px;
    width: 90%;
    max-height: 85vh;
    overflow-y: auto;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
`;

const MatchList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 12px;
    margin-top: 16px;
`;

const MatchCard = styled.div`
    padding: 16px;
    border: 2px solid ${props => props.selected ? '#f39c12' : '#e9ecef'};
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    background: ${props => props.selected ? '#fff8e1' : 'white'};

    &:hover {
        border-color: #f39c12;
        background: #fffcf5;
    }

    .match-header {
        display: flex;
        justify-content: space-between;
        align-items: flex-start;
        margin-bottom: 8px;
    }

    .match-name {
        font-weight: 600;
        font-size: 15px;
        color: #333;
    }

    .match-company {
        font-size: 13px;
        color: #666;
        margin-top: 2px;
    }

    .match-email {
        font-size: 13px;
        color: #004085;
        margin-top: 4px;
    }

    .match-date {
        font-size: 11px;
        color: #999;
        background: #f8f9fa;
        padding: 2px 8px;
        border-radius: 10px;
    }
`;

const MatchInfo = styled.div`
    background: #e7f3ff;
    padding: 12px 16px;
    border-radius: 8px;
    margin-bottom: 16px;
    font-size: 13px;
    color: #004085;

    strong {
        display: block;
        margin-bottom: 4px;
    }
`;

const NoMatchesMessage = styled.div`
    text-align: center;
    padding: 32px;
    color: #666;
    font-size: 14px;
`;

const SearchingIndicator = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 24px;
    color: #666;
    font-size: 14px;
`;

// Project matching styled components
const ProjectMatchButton = styled.button`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    background: ${props => props.hasProject ? '#28a745' : props.hasMatches ? '#17a2b8' : '#6c757d'};
    color: white;
    border: none;
    border-radius: 4px;
    font-size: 11px;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        opacity: 0.9;
    }

    &:disabled {
        opacity: 0.5;
        cursor: not-allowed;
    }
`;

const ProjectMatchBadge = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 4px 8px;
    background: ${props => props.linked ? '#d4edda' : '#f8f9fa'};
    color: ${props => props.linked ? '#155724' : '#6c757d'};
    border-radius: 12px;
    font-size: 11px;
    font-weight: 500;
`;

const ProjectMatchCard = styled.div`
    padding: 16px;
    border: 2px solid ${props => props.selected ? '#28a745' : '#e9ecef'};
    border-radius: 12px;
    cursor: pointer;
    transition: all 0.2s ease;
    background: ${props => props.selected ? '#e8f5e9' : 'white'};
    display: flex;
    gap: 16px;

    &:hover {
        border-color: #28a745;
        background: #f1f8e9;
    }

    .project-thumbnail {
        width: 120px;
        height: 90px;
        background-size: cover;
        background-position: center;
        background-color: #f8f9fa;
        border-radius: 8px;
        flex-shrink: 0;
        border: 1px solid #eee;
    }

    .project-info {
        flex: 1;
        display: flex;
        flex-direction: column;
        justify-content: center;
    }

    .project-name {
        font-weight: 600;
        font-size: 15px;
        color: #333;
        margin-bottom: 4px;
    }

    .project-company {
        font-size: 13px;
        color: #666;
        margin-bottom: 4px;
    }

    .project-type {
        font-size: 12px;
        color: #999;
        background: #f8f9fa;
        padding: 2px 8px;
        border-radius: 10px;
        display: inline-block;
    }
`;

const ConfirmProjectButton = styled.button`
    width: 100%;
    padding: 12px;
    background: linear-gradient(135deg, #28a745, #20c997);
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
    margin-top: 16px;

    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(40, 167, 69, 0.4);
    }

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
        transform: none;
    }
`;

const ProjectThumbnailSmall = styled.div`
    width: 40px;
    height: 30px;
    background-size: cover;
    background-position: center;
    background-color: #f0f0f0;
    border-radius: 4px;
    border: 1px solid #ddd;
    display: inline-block;
    vertical-align: middle;
    margin-right: 8px;
`;

// All FD100 edition event IDs (all in Supabase winners table)
const PAST_EDITION_IDS = {
    'ec25d885-3dcf-455c-93b2-26041cb842e4': '1ère édition',
    '417b2020-721a-4c55-bd9f-d86a913f26ad': '2ème édition',
    'a3b7c9d1-e5f6-4a2b-8c3d-1e5f6a2b8c3d': '3ème édition'
};

const FD100PastWinners = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    const eventId = searchParams.get('id');

    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [winners, setWinners] = useState([]);
    const [eventInfo, setEventInfo] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [lastUpdate, setLastUpdate] = useState(null);
    const [editModal, setEditModal] = useState(null);
    const [saving, setSaving] = useState(false);

    // Company matching state
    const [matchModal, setMatchModal] = useState(null); // { winner, matches, searching, selectedMatch }
    const [potentialMatchesCache, setPotentialMatchesCache] = useState({}); // Cache matches by winner.id
    const [searchingMatches, setSearchingMatches] = useState({}); // Track which winners are being searched

    // Project matching state
    const [projectMatchModal, setProjectMatchModal] = useState(null); // { winner, matches, searching, selectedMatch }
    const [potentialProjectsCache, setPotentialProjectsCache] = useState({}); // Cache project matches by winner.id

    useEffect(() => {
        if (eventId && PAST_EDITION_IDS[eventId]) {
            fetchData();
        }
    }, [eventId]);

    const fetchData = async () => {
        try {
            setLoading(true);

            await fetchSupabaseData();

            setLastUpdate(new Date());
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Erreur lors du chargement des données');
        } finally {
            setLoading(false);
        }
    };

    const fetchSupabaseData = async () => {
        // Fetch event info
        const { data: eventData, error: eventError } = await supabase
            .from('events')
            .select('*')
            .eq('id', eventId)
            .single();

        if (eventError) {
            console.error('Error fetching event:', eventError);
        } else {
            setEventInfo(eventData);
        }

        // Fetch winners for this event
        const { data: winnersData, error: winnersError } = await supabase
            .from('winners')
            .select('*')
            .eq('event_id', eventId)
            .order('company_name', { ascending: true });

        if (winnersError) {
            console.error('Error fetching winners:', winnersError);
            toast.error('Erreur lors du chargement des lauréats');
        } else {
            setWinners(winnersData || []);
        }
    };

    const handleRefresh = async () => {
        setRefreshing(true);
        await fetchData();
        setRefreshing(false);
        toast.success('Données actualisées');
    };

    const openEditModal = (winner) => {
        setEditModal({ ...winner });
    };

    const closeEditModal = () => {
        setEditModal(null);
    };

    const handleEditChange = (field, value) => {
        setEditModal(prev => ({ ...prev, [field]: value }));
    };

    const handleSaveEdit = async () => {
        if (!editModal) return;

        setSaving(true);
        try {
            const { error } = await supabase
                .from('winners')
                .update({
                    project_name: editModal.project_name,
                    company_name: editModal.company_name,
                    first_name: editModal.first_name,
                    last_name: editModal.last_name,
                    email: editModal.email,
                    phone: editModal.phone
                })
                .eq('id', editModal.id);

            if (error) {
                console.error('Error updating winner:', error);
                toast.error('Erreur lors de la sauvegarde');
            } else {
                toast.success('Lauréat mis à jour');
                closeEditModal();
                fetchData();
            }
        } catch (error) {
            console.error('Error saving:', error);
            toast.error('Erreur lors de la sauvegarde');
        } finally {
            setSaving(false);
        }
    };

    // Search for potential matches based on company name
    const searchForMatches = async (winner) => {
        if (!winner.company_name || winner.user_id) return; // Skip if no company name or already matched

        // Check cache first
        if (potentialMatchesCache[winner.id]) {
            setMatchModal({
                winner,
                matches: potentialMatchesCache[winner.id],
                searching: false,
                selectedMatch: null
            });
            return;
        }

        setMatchModal({
            winner,
            matches: [],
            searching: true,
            selectedMatch: null
        });

        try {
            const response = await SearchPotentialMatches(winner.company_name);
            const matches = response.data?.matches || [];

            // Cache the result
            setPotentialMatchesCache(prev => ({
                ...prev,
                [winner.id]: matches
            }));

            setMatchModal(prev => ({
                ...prev,
                matches,
                searching: false
            }));
        } catch (error) {
            console.error('Error searching for matches:', error);
            toast.error('Erreur lors de la recherche de correspondances');
            setMatchModal(prev => ({
                ...prev,
                searching: false
            }));
        }
    };

    // Handle selecting a match
    const handleSelectMatch = (match) => {
        setMatchModal(prev => ({
            ...prev,
            selectedMatch: prev.selectedMatch?.user_id === match.user_id ? null : match
        }));
    };

    // Confirm and link the selected match
    const handleConfirmMatch = async () => {
        if (!matchModal?.winner || !matchModal?.selectedMatch) return;

        setSaving(true);
        try {
            // Update Supabase winners table directly from frontend
            // User is already verified via SearchPotentialMatches which queries MongoDB
            const { error } = await supabase
                .from('winners')
                .update({
                    email: matchModal.selectedMatch.email.toLowerCase(),
                    user_id: matchModal.selectedMatch.user_id
                })
                .eq('id', matchModal.winner.id);

            if (error) {
                console.error('Error updating winner in Supabase:', error);
                toast.error('Erreur lors de la mise à jour: ' + error.message);
                return;
            }

            toast.success(`Lauréat lié à ${matchModal.selectedMatch.first_name || ''} ${matchModal.selectedMatch.last_name || ''}`);

            // Clear cache for this winner since they're now matched
            setPotentialMatchesCache(prev => {
                const newCache = { ...prev };
                delete newCache[matchModal.winner.id];
                return newCache;
            });

            setMatchModal(null);
            fetchData(); // Refresh the list
        } catch (error) {
            console.error('Error linking winner:', error);
            toast.error('Erreur lors de la liaison du lauréat');
        } finally {
            setSaving(false);
        }
    };

    // Close match modal
    const closeMatchModal = () => {
        setMatchModal(null);
    };

    // Search for potential project matches based on project name using backend API
    const searchForProjects = async (winner) => {
        if (!winner.project_name || winner.project_id) return; // Skip if no project name or already matched

        // Check cache first
        if (potentialProjectsCache[winner.id]) {
            setProjectMatchModal({
                winner,
                matches: potentialProjectsCache[winner.id],
                searching: false,
                selectedMatch: null
            });
            return;
        }

        setProjectMatchModal({
            winner,
            matches: [],
            searching: true,
            selectedMatch: null
        });

        try {
            const response = await SearchPotentialProjects(winner.project_name, winner.company_name);
            const matches = response.data?.matches || [];

            // Cache the result
            setPotentialProjectsCache(prev => ({
                ...prev,
                [winner.id]: matches
            }));

            setProjectMatchModal(prev => ({
                ...prev,
                matches,
                searching: false
            }));
        } catch (error) {
            console.error('Error searching for projects:', error);
            toast.error('Erreur lors de la recherche de projets');
            setProjectMatchModal(prev => ({
                ...prev,
                searching: false
            }));
        }
    };

    // Handle selecting a project match
    const handleSelectProjectMatch = (match) => {
        setProjectMatchModal(prev => ({
            ...prev,
            selectedMatch: prev.selectedMatch?.project_id === match.project_id ? null : match
        }));
    };

    // Confirm and link the selected project
    const handleConfirmProjectMatch = async () => {
        if (!projectMatchModal?.winner || !projectMatchModal?.selectedMatch) return;

        setSaving(true);
        try {
            // Update Supabase winners table with project_id and company_id
            const { error } = await supabase
                .from('winners')
                .update({
                    project_id: projectMatchModal.selectedMatch.project_id,
                    company_id: projectMatchModal.selectedMatch.company_id
                })
                .eq('id', projectMatchModal.winner.id);

            if (error) {
                console.error('Error updating winner in Supabase:', error);
                toast.error('Erreur lors de la mise à jour: ' + error.message);
                return;
            }

            toast.success(`Projet lié: ${projectMatchModal.selectedMatch.project_name}`);

            // Clear cache for this winner since they're now matched
            setPotentialProjectsCache(prev => {
                const newCache = { ...prev };
                delete newCache[projectMatchModal.winner.id];
                return newCache;
            });

            setProjectMatchModal(null);
            fetchData(); // Refresh the list
        } catch (error) {
            console.error('Error linking project:', error);
            toast.error('Erreur lors de la liaison du projet');
        } finally {
            setSaving(false);
        }
    };

    // Close project match modal
    const closeProjectMatchModal = () => {
        setProjectMatchModal(null);
    };

    // Get project thumbnail URL (backend returns relative paths like "userfiles/...")
    const getProjectThumbnailUrl = (thumbnailPath) => {
        if (!thumbnailPath) return null;
        return process.env.REACT_APP_MEDIA_BASE_URL + thumbnailPath;
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return 'Jamais connecté';
        const date = new Date(dateString);
        return date.toLocaleDateString('fr-FR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    const getStats = () => {
        const total = winners.length;
        const withEmail = winners.filter(w => w.email).length;
        const onboarded = winners.filter(w => w.user_id).length;
        const pending = total - onboarded;
        const withProject = winners.filter(w => w.project_id).length;

        return { total, withEmail, onboarded, pending, withProject };
    };

    const filteredWinners = winners.filter(winner => {
        const search = searchTerm.toLowerCase();
        return (
            winner.first_name?.toLowerCase().includes(search) ||
            winner.last_name?.toLowerCase().includes(search) ||
            winner.company_name?.toLowerCase().includes(search) ||
            winner.project_name?.toLowerCase().includes(search) ||
            winner.email?.toLowerCase().includes(search)
        );
    });

    const exportToCSV = () => {
        const baseHeaders = [
            'Projet',
            'Entreprise',
            'Prénom',
            'Nom',
            'Email',
            'Téléphone',
            'Sur la plateforme',
            'Projet dans annuaire'
        ];
        const extraHeaders = false ? ['Catégorie', 'Type', 'Nb médias'] : [];
        const headers = [...baseHeaders, ...extraHeaders];

        const rows = winners.map(winner => {
            const baseRow = [
                winner.project_name || '',
                winner.company_name || '',
                winner.first_name || '',
                winner.last_name || '',
                winner.email || '',
                winner.phone || '',
                winner.user_id ? 'Oui' : 'Non',
                winner.project_id ? 'Oui' : 'Non'
            ];
            const extraRow = false ? [
                winner._category || '',
                winner._type || '',
                winner._mediasCount || 0
            ] : [];
            return [...baseRow, ...extraRow];
        });

        const csvContent = [
            headers.join(','),
            ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = `laureats_fd100_${PAST_EDITION_IDS[eventId]?.replace(' ', '_')}_${new Date().toISOString().split('T')[0]}.csv`;
        link.click();

        toast.success('Export CSV téléchargé');
    };

    // Check if this is a valid past edition
    if (!eventId || !PAST_EDITION_IDS[eventId]) {
        return (
            <Container>
                <BackButton onClick={() => navigate('/admin/events')}>
                    ← Retour aux événements
                </BackButton>
                <Header>
                    <h1>Événement non trouvé</h1>
                    <div className="subtitle">Cet événement n'est pas une édition passée du FD100.</div>
                </Header>
            </Container>
        );
    }

    if (loading) return <Spinner />;

    const stats = getStats();
    const editionName = PAST_EDITION_IDS[eventId];

    return (
        <Container>
            <BackButton onClick={() => navigate('/admin/events')}>
                ← Retour aux événements
            </BackButton>

            <Header>
                <h1>Lauréats FD100 - {editionName}</h1>
                <div className="subtitle">
                    {eventInfo?.title_fr || `French Design 100 - ${editionName}`}
                </div>
            </Header>

            <StatsSection>
                <StatCard>
                    <div className="value">{stats.total}</div>
                    <div className="label">Lauréats total</div>
                </StatCard>
                <StatCard color="#e7f3ff" textColor="#004085">
                    <div className="value">{stats.withEmail}</div>
                    <div className="label">Avec email</div>
                </StatCard>
                <StatCard color="#d4edda" textColor="#155724">
                    <div className="value">{stats.onboarded}</div>
                    <div className="label">Sur la plateforme</div>
                </StatCard>
                <StatCard color="#e8f5e9" textColor="#2e7d32">
                    <div className="value">{stats.withProject}</div>
                    <div className="label">Projet dans l'annuaire</div>
                </StatCard>
                <StatCard color="#fff3cd" textColor="#856404">
                    <div className="value">{stats.pending}</div>
                    <div className="label">En attente d'inscription</div>
                </StatCard>
            </StatsSection>

            <TableSection>
                <TableHeader>
                    <h2>Liste des lauréats ({filteredWinners.length})</h2>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <SearchInput
                            type="text"
                            placeholder="Rechercher par nom, entreprise, projet..."
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                        <RefreshButton onClick={handleRefresh} disabled={refreshing} style={{ marginLeft: 12 }}>
                            {refreshing ? 'Actualisation...' : '↻ Actualiser'}
                        </RefreshButton>
                        <ExportButton onClick={exportToCSV}>
                            ↓ Export CSV
                        </ExportButton>
                    </div>
                </TableHeader>

                <div style={{ overflowX: 'auto' }}>
                    <Table>
                        <thead>
                            <tr>
                                <th>Projet</th>
                                {false && <th>Catégorie</th>}
                                <th>Entreprise</th>
                                <th>Créateur</th>
                                <th>Email</th>
                                {!false && <th>Téléphone</th>}
                                <th>Statut</th>
                                {!false && <th>Correspondances</th>}
                                <th>Projet Annuaire</th>
                                {!false && <th>Actions</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {filteredWinners.map((winner) => (
                                <tr key={winner.id}>
                                    <td style={{ maxWidth: 200 }}>
                                        <strong>{winner.project_name || '-'}</strong>
                                    </td>
                                    {false && (
                                        <td style={{ fontSize: 12 }}>
                                            <span style={{
                                                padding: '3px 8px',
                                                borderRadius: 12,
                                                background: '#e3f2fd',
                                                color: '#1976d2',
                                                fontSize: 11
                                            }}>
                                                {winner._category || '-'}
                                            </span>
                                        </td>
                                    )}
                                    <td style={{ maxWidth: 180 }}>
                                        {winner.company_name || '-'}
                                    </td>
                                    <td>
                                        {winner.first_name} {winner.last_name}
                                    </td>
                                    <td style={{ maxWidth: 200 }}>
                                        {winner.email ? (
                                            <a href={`mailto:${winner.email}`} style={{ color: '#004085' }}>
                                                {winner.email}
                                            </a>
                                        ) : '-'}
                                    </td>
                                    {!false && <td>{winner.phone || '-'}</td>}
                                    <td>
                                        <StatusBadge className={winner.user_id ? 'onboarded' : 'pending'}>
                                            {winner.user_id ? '✓ Inscrit' : '○ En attente'}
                                        </StatusBadge>
                                    </td>
                                    {!false && (
                                        <td>
                                            {winner.user_id ? (
                                                <MatchBadge count={0}>Déjà lié</MatchBadge>
                                            ) : winner.company_name ? (
                                                potentialMatchesCache[winner.id] ? (
                                                    <MatchButton
                                                        hasMatches={potentialMatchesCache[winner.id].length > 0}
                                                        onClick={() => searchForMatches(winner)}
                                                    >
                                                        {potentialMatchesCache[winner.id].length > 0
                                                            ? `${potentialMatchesCache[winner.id].length} trouvé(s)`
                                                            : 'Aucun'}
                                                    </MatchButton>
                                                ) : (
                                                    <MatchButton
                                                        onClick={() => searchForMatches(winner)}
                                                        disabled={searchingMatches[winner.id]}
                                                    >
                                                        {searchingMatches[winner.id] ? '...' : '🔍 Chercher'}
                                                    </MatchButton>
                                                )
                                            ) : (
                                                <MatchBadge count={0}>Pas d'entreprise</MatchBadge>
                                            )}
                                        </td>
                                    )}
                                    <td>
                                        {winner.project_id ? (
                                            <ProjectMatchBadge linked>
                                                <a
                                                    href={`/project?pid=${winner.project_id}&cid=${winner.company_id}`}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{ color: 'inherit', textDecoration: 'none' }}
                                                >
                                                    ✓ Voir projet
                                                </a>
                                            </ProjectMatchBadge>
                                        ) : winner.project_name ? (
                                            !false && potentialProjectsCache[winner.id] ? (
                                                <ProjectMatchButton
                                                    hasMatches={potentialProjectsCache[winner.id].length > 0}
                                                    onClick={() => searchForProjects(winner)}
                                                >
                                                    {potentialProjectsCache[winner.id].length > 0
                                                        ? `${potentialProjectsCache[winner.id].length} trouvé(s)`
                                                        : 'Aucun'}
                                                </ProjectMatchButton>
                                            ) : !false ? (
                                                <ProjectMatchButton
                                                    onClick={() => searchForProjects(winner)}
                                                >
                                                    🔍 Chercher
                                                </ProjectMatchButton>
                                            ) : (
                                                <ProjectMatchBadge>{winner.project_id ? '✓' : '○ Non lié'}</ProjectMatchBadge>
                                            )
                                        ) : (
                                            <ProjectMatchBadge>Pas de projet</ProjectMatchBadge>
                                        )}
                                    </td>
                                    {!false && (
                                        <td>
                                            <ActionButton onClick={() => openEditModal(winner)}>
                                                ✎ Modifier
                                            </ActionButton>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </div>

                <LastUpdate>
                    Dernière actualisation : {lastUpdate?.toLocaleString('fr-FR')}
                </LastUpdate>
            </TableSection>

            {/* Edit Modal */}
            {editModal && (
                <ModalOverlay onClick={closeEditModal}>
                    <ModalContent onClick={e => e.stopPropagation()}>
                        <ModalHeader>
                            <h3>Modifier le lauréat</h3>
                            <CloseButton onClick={closeEditModal}>&times;</CloseButton>
                        </ModalHeader>

                        <FormGroup>
                            <label>Nom du projet</label>
                            <input
                                type="text"
                                value={editModal.project_name || ''}
                                onChange={(e) => handleEditChange('project_name', e.target.value)}
                            />
                        </FormGroup>

                        <FormGroup>
                            <label>Entreprise</label>
                            <input
                                type="text"
                                value={editModal.company_name || ''}
                                onChange={(e) => handleEditChange('company_name', e.target.value)}
                            />
                        </FormGroup>

                        <FormGroup>
                            <label>Prénom</label>
                            <input
                                type="text"
                                value={editModal.first_name || ''}
                                onChange={(e) => handleEditChange('first_name', e.target.value)}
                            />
                        </FormGroup>

                        <FormGroup>
                            <label>Nom</label>
                            <input
                                type="text"
                                value={editModal.last_name || ''}
                                onChange={(e) => handleEditChange('last_name', e.target.value)}
                            />
                        </FormGroup>

                        <FormGroup>
                            <label>Email</label>
                            <input
                                type="email"
                                value={editModal.email || ''}
                                onChange={(e) => handleEditChange('email', e.target.value)}
                            />
                        </FormGroup>

                        <FormGroup>
                            <label>Téléphone</label>
                            <input
                                type="tel"
                                value={editModal.phone || ''}
                                onChange={(e) => handleEditChange('phone', e.target.value)}
                            />
                        </FormGroup>

                        <SaveButton onClick={handleSaveEdit} disabled={saving}>
                            {saving ? 'Enregistrement...' : 'Enregistrer les modifications'}
                        </SaveButton>
                    </ModalContent>
                </ModalOverlay>
            )}

            {/* Match Modal */}
            {matchModal && (
                <ModalOverlay onClick={closeMatchModal}>
                    <MatchModalContent onClick={e => e.stopPropagation()}>
                        <ModalHeader>
                            <h3>Correspondances potentielles</h3>
                            <CloseButton onClick={closeMatchModal}>&times;</CloseButton>
                        </ModalHeader>

                        <MatchInfo>
                            <strong>Lauréat recherché :</strong>
                            {matchModal.winner.first_name} {matchModal.winner.last_name} - {matchModal.winner.company_name}
                            <br />
                            <span style={{ fontSize: '12px', color: '#666' }}>
                                Email actuel : {matchModal.winner.email || 'Non renseigné'}
                            </span>
                        </MatchInfo>

                        {matchModal.searching ? (
                            <SearchingIndicator>
                                <span>Recherche en cours...</span>
                            </SearchingIndicator>
                        ) : matchModal.matches.length === 0 ? (
                            <NoMatchesMessage>
                                Aucune correspondance trouvée pour "{matchModal.winner.company_name}"
                                <br />
                                <span style={{ fontSize: '12px', marginTop: '8px', display: 'block' }}>
                                    Aucun utilisateur de la plateforme n'est associé à une entreprise avec ce nom.
                                </span>
                            </NoMatchesMessage>
                        ) : (
                            <>
                                <div style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
                                    {matchModal.matches.length} utilisateur(s) trouvé(s) - classés par dernière connexion
                                </div>
                                <MatchList>
                                    {matchModal.matches.map(match => (
                                        <MatchCard
                                            key={match.user_id}
                                            selected={matchModal.selectedMatch?.user_id === match.user_id}
                                            onClick={() => handleSelectMatch(match)}
                                        >
                                            <div className="match-header">
                                                <div>
                                                    <div className="match-name">
                                                        {match.hsProfile?.firstname || match.first_name || ''} {match.hsProfile?.lastname || match.last_name || ''}
                                                    </div>
                                                    <div className="match-company">
                                                        {match.hsCompany?.name || match.hsProfile?.company || 'Entreprise inconnue'}
                                                    </div>
                                                </div>
                                                <div className="match-date">
                                                    Connexion : {formatDate(match.last_connection_date)}
                                                </div>
                                            </div>
                                            <div className="match-email">
                                                {match.email}
                                            </div>
                                        </MatchCard>
                                    ))}
                                </MatchList>

                                {matchModal.selectedMatch && (
                                    <SaveButton onClick={handleConfirmMatch} disabled={saving}>
                                        {saving ? 'Liaison en cours...' : `Lier à ${matchModal.selectedMatch.email}`}
                                    </SaveButton>
                                )}
                            </>
                        )}
                    </MatchModalContent>
                </ModalOverlay>
            )}

            {/* Project Match Modal */}
            {projectMatchModal && (
                <ModalOverlay onClick={closeProjectMatchModal}>
                    <MatchModalContent onClick={e => e.stopPropagation()}>
                        <ModalHeader>
                            <h3>Projets correspondants dans l'annuaire</h3>
                            <CloseButton onClick={closeProjectMatchModal}>&times;</CloseButton>
                        </ModalHeader>

                        <MatchInfo>
                            <strong>Projet recherché :</strong>
                            {projectMatchModal.winner.project_name}
                            <br />
                            <span style={{ fontSize: '12px', color: '#666' }}>
                                Entreprise : {projectMatchModal.winner.company_name || 'Non renseignée'}
                            </span>
                        </MatchInfo>

                        {projectMatchModal.searching ? (
                            <SearchingIndicator>
                                <span>Recherche en cours...</span>
                            </SearchingIndicator>
                        ) : projectMatchModal.matches.length === 0 ? (
                            <NoMatchesMessage>
                                Aucun projet correspondant trouvé pour "{projectMatchModal.winner.project_name}"
                                <br />
                                <span style={{ fontSize: '12px', marginTop: '8px', display: 'block' }}>
                                    Aucun projet de l'annuaire ne correspond à ce nom.
                                </span>
                            </NoMatchesMessage>
                        ) : (
                            <>
                                <div style={{ fontSize: '13px', color: '#666', marginBottom: '12px' }}>
                                    {projectMatchModal.matches.length} projet(s) trouvé(s) - classés par pertinence
                                </div>
                                <MatchList>
                                    {projectMatchModal.matches.map(match => (
                                        <ProjectMatchCard
                                            key={match.project_id}
                                            selected={projectMatchModal.selectedMatch?.project_id === match.project_id}
                                            onClick={() => handleSelectProjectMatch(match)}
                                        >
                                            <div
                                                className="project-thumbnail"
                                                style={{
                                                    backgroundImage: match.thumbnail
                                                        ? `url(${getProjectThumbnailUrl(match.thumbnail)})`
                                                        : 'none'
                                                }}
                                            />
                                            <div className="project-info">
                                                <div className="project-name">
                                                    {match.project_name || match.project_name_en || 'Sans nom'}
                                                </div>
                                                {match.company_name && (
                                                    <div className="project-company">
                                                        {match.company_name}
                                                    </div>
                                                )}
                                                {match.type && (
                                                    <span className="project-type">{match.type}</span>
                                                )}
                                            </div>
                                        </ProjectMatchCard>
                                    ))}
                                </MatchList>

                                {projectMatchModal.selectedMatch && (
                                    <ConfirmProjectButton onClick={handleConfirmProjectMatch} disabled={saving}>
                                        {saving ? 'Liaison en cours...' : `Confirmer le projet "${projectMatchModal.selectedMatch.project_name}"`}
                                    </ConfirmProjectButton>
                                )}
                            </>
                        )}
                    </MatchModalContent>
                </ModalOverlay>
            )}
        </Container>
    );
};

export default FD100PastWinners;
